import { Vector2 } from 'three';
import type { WebGLRenderer } from 'three';

import PlanogramPoint from 'shared/utils/PlanogramPoint';
import {
  debugCommand,
  debugFloatPrameter,
  debugIntParameter,
  debugLog,
  hasDebugFlag,
} from 'shared/utils/debug';

import ImageRegistry from './ImageRegistry';
import { TileMap } from './TileMap';
import LodMaterial from './LodMaterial';
import PhysicalTextures from './PhysicalTextures';
import TileLoader from './TileLoader';
import { TextureCache } from './TextureCache';
import LodDebug from './LodDebug';
import type { ExtraImageData, ImageId, ImageLodData, LodId } from './interfaces';

const LOD_SUPERSAMPLE = debugFloatPrameter('LOD_SUPERSAMPLE', 1);

export interface LodParameters {
  tileMapResolution: number;
  physicalTextureCount: number;
  physicalTextureResolution: number;
  simultaneousTilesLoading: number;
  targetPixelRatio: number;
  unloadedTileBias: number;
  cdnUrl: string;
  blacklistDuration: number;
  frameUploadLimit: number;
}

const defaultParameters: LodParameters = {
  tileMapResolution: 2048,
  physicalTextureCount: 7,
  physicalTextureResolution: 4096,
  simultaneousTilesLoading: 8,
  targetPixelRatio: 1.1,
  unloadedTileBias: 5,
  cdnUrl: '',
  blacklistDuration: 2 * 1000,
  // TODO: setting this parameter below 4 produces renders of incorrect tiles, e. g. on https://jack-wood-guitar.cozmos.com/en/shredders_university_audio
  // the tile map writes to wait for physical texture writes, but somehow it still desyncs. Does it need to wait an extra frame?
  frameUploadLimit: 5,
};

function fallbackLodParameters(input: Partial<LodParameters>, gl: WebGLRenderer): LodParameters {
  const defaultedParameters = {
    ...defaultParameters,
    ...input,
  };
  const parameters = {
    ...defaultedParameters,
    tileMapResolution: debugIntParameter(
      'TILEMAP_RESOLUTION',
      defaultedParameters.tileMapResolution,
    ),
    physicalTextureCount: debugIntParameter(
      'PHYSICAL_TEXTURE_COUNT',
      defaultedParameters.physicalTextureCount,
    ),
    physicalTextureResolution: debugIntParameter(
      'PHYSICAL_TEXTURE_RESOLUTION',
      defaultedParameters.physicalTextureResolution,
    ),
    simultaneousTilesLoading: debugIntParameter(
      'SIMULTANEOUS_TILES_LOADING',
      defaultedParameters.simultaneousTilesLoading,
    ),
    targetPixelRatio: debugFloatPrameter(
      'TARGET_PIXEL_RATIO',
      defaultedParameters.targetPixelRatio,
    ),
    unloadedTileBias: debugFloatPrameter(
      'UNLOADED_TILE_BIAS',
      defaultedParameters.unloadedTileBias,
    ),
    blacklistDuration: debugIntParameter(
      'BLACKLIST_DURATION',
      defaultedParameters.blacklistDuration,
    ),
    frameUploadLimit: debugIntParameter('FRAME_UPLOAD_LIMIT', defaultedParameters.frameUploadLimit),
  };
  parameters.physicalTextureResolution = Math.min(
    gl.capabilities.maxTextureSize,
    parameters.physicalTextureResolution,
  );
  parameters.tileMapResolution = Math.min(
    gl.capabilities.maxTextureSize,
    parameters.tileMapResolution,
  );
  parameters.physicalTextureCount = Math.min(
    gl.capabilities.maxTextures - 1,
    parameters.physicalTextureCount,
  );
  return parameters;
}

export default class LodProvider {
  private imageRegistry: ImageRegistry;
  private tileMap: TileMap;
  private physicalTextures: PhysicalTextures;
  private tileLoader: TileLoader;
  private textureCache: TextureCache;
  private materials: Map<ImageId, LodMaterial> = new Map();
  private parameters: LodParameters;
  private focusPoint: PlanogramPoint = new PlanogramPoint();
  private pixelRatio: number = 0;
  private loaded = false;

  public readonly debug?: LodDebug;

  constructor(renderer: WebGLRenderer, parameterOverrides: Partial<LodParameters>) {
    const parameters = fallbackLodParameters(parameterOverrides, renderer);
    this.parameters = parameters;

    this.tileMap = new TileMap(parameters.tileMapResolution, renderer);
    this.physicalTextures = new PhysicalTextures(
      parameters.physicalTextureCount,
      parameters.physicalTextureResolution,
      renderer,
      parameters.frameUploadLimit,
    );
    this.textureCache = new TextureCache();
    this.tileLoader = new TileLoader(
      this.tileMap,
      this.textureCache,
      this.physicalTextures,
      parameters.cdnUrl,
      parameters.simultaneousTilesLoading,
      parameters.unloadedTileBias,
      parameters.blacklistDuration,
      parameters.targetPixelRatio,
    );
    this.imageRegistry = new ImageRegistry(this.tileMap, this.tileLoader);
    {
      const weakImageRegistry = new WeakRef(this.imageRegistry);
      debugCommand('imageRegistry', () => weakImageRegistry.deref());
    }

    this.imageRegistry.onImageReady((id: ImageId) => {
      const material = this.materials.get(id) ?? this.makeMaterial();
      this.materials.set(id, material);
      material.setItemUniforms(this.imageRegistry.getImageUniforms(id)!);
    });

    if (hasDebugFlag('physicalTextures')) this.debug = new LodDebug(this.physicalTextures.textures);
  }

  update() {
    const noWork = [
      this.tileMap.update(),
      this.physicalTextures.update(),
      this.tileLoader.update(this.focusPoint, this.pixelRatio),
    ].every(it => it);
    if (noWork && !this.loaded && !this.imageRegistry.isLoading()) {
      this.loadedListeners.forEach(it => it());
      debugLog(`Lod Provider finished pre-loading`);
      this.loaded = true;
    }
    this.debug?.update();
  }

  updateImageList(
    images: Map<ImageId, ExtraImageData>,
    lodLoader: (ids: LodId[]) => Promise<ImageLodData[]>,
  ) {
    this.imageRegistry.updateImages(images, lodLoader).forEach(id => {
      const uniforms = this.imageRegistry.getImageUniforms(id);
      if (uniforms !== undefined) this.materials.get(id)?.setItemUniforms(uniforms);
    });
  }

  updateCamera(position: PlanogramPoint, pixelsToPlanogramRatio: number) {
    this.focusPoint.copy(position);
    this.pixelRatio = pixelsToPlanogramRatio * LOD_SUPERSAMPLE;
  }

  private makeMaterial() {
    return new LodMaterial(
      this.tileMap,
      this.physicalTextures.textures,
      this.parameters.physicalTextureResolution,
      hasDebugFlag('lodColors'),
    );
  }

  getMaterial(id: ImageId): LodMaterial {
    let material = this.materials.get(id);
    if (material === undefined) {
      material = this.makeMaterial();
      this.materials.set(id, material);
    }
    return material;
  }

  getContentRatio(id: ImageId): Vector2 {
    const ratio = this.imageRegistry.getContentRatio(id);
    if (ratio === undefined) return new Vector2(1, 1);
    return ratio;
  }

  private loadedListeners = new Set<() => void>();

  addLoadedListener(listener: () => void) {
    this.loadedListeners.add(listener);
  }

  removeLoadedListener(listener: () => void) {
    this.loadedListeners.delete(listener);
  }

  resetLoadedState() {
    this.loaded = false;
  }

  isLoaded(): boolean {
    return this.loaded;
  }

  dispose() {
    this.debug?.dispose();
    this.tileLoader.dispose();
    this.physicalTextures.dispose();
    this.textureCache.dispose();
    this.tileMap.dispose();
  }
}
